import { findColumnInSections, locateRow } from "../find";
import { getSectionSlotInsertBounds } from "../row-policy";
import { isGroup } from "../sections";
import { blockIdsInDragItem } from "../selection/block-bulk-ops";
import { rowIdsInDragItem } from "../selection/row-bulk-ops";
import type { DocumentSection, Row } from "../types";
import type { DragData, DropData } from "./types";

function rowHasSingleBlock(row: Row, blockId: string): boolean {
  let count = 0;
  for (const column of row.columns) {
    for (const block of column.blocks) {
      if (block.id === blockId) {
        count += 1;
      } else {
        return false;
      }
    }
  }
  return count === 1;
}

function isBlockSectionSlotNoOp(
  sections: DocumentSection[],
  slotIndex: number,
  drag: Extract<DragData, { kind: 'item-block' }>,
): boolean {
  const located = locateRow(sections, drag.rowId);
  if (!located || located.location.parent !== 'document') {
    return false;
  }
  if (!rowHasSingleBlock(located.row, drag.blockId)) {
    return false;
  }
  return isAdjacentNoOpSlot(slotIndex, located.location.sectionIndex);
}

function isBlockGroupRowSlotNoOp(
  sections: DocumentSection[],
  slot: Extract<DropData, { kind: 'group-row-slot' }>,
  drag: Extract<DragData, { kind: 'item-block' }>,
): boolean {
  const located = locateRow(sections, drag.rowId);
  if (
    !located ||
    located.location.parent !== 'group' ||
    located.location.groupId !== slot.groupId
  ) {
    return false;
  }
  if (!rowHasSingleBlock(located.row, drag.blockId)) {
    return false;
  }
  return isAdjacentNoOpSlot(slot.index, located.location.rowIndex);
}

export function isBlockDrag(data: DragData | undefined): boolean {
  return data?.kind === 'palette-block' || data?.kind === 'item-block';
}

export function isRowDrag(data: DragData | undefined): boolean {
  return (
    data?.kind === 'palette-row' ||
    data?.kind === 'palette-section' ||
    data?.kind === 'item-row'
  );
}

export function isSectionDrag(data: DragData | undefined): boolean {
  return (
    isRowDrag(data) ||
    data?.kind === 'item-group' ||
    data?.kind === 'palette-group'
  );
}

/** Reordering in-place: slots at fromIndex and fromIndex+1 insert back to the same position. */
export function isAdjacentNoOpSlot(
  slotIndex: number,
  fromIndex: number,
): boolean {
  return slotIndex === fromIndex || slotIndex === fromIndex + 1;
}

export function isSectionSlotEnabled(
  sections: DocumentSection[],
  slotIndex: number,
  drag: DragData | undefined,
): boolean {
  if (!drag) {
    return false;
  }

  const { min, max } = getSectionSlotInsertBounds(sections);
  if (slotIndex < min || slotIndex > max) {
    return false;
  }

  if (isBlockDrag(drag)) {
    if (
      drag.kind === 'item-block' &&
      isBlockSectionSlotNoOp(sections, slotIndex, drag)
    ) {
      return false;
    }
    return true;
  }

  if (!isSectionDrag(drag)) {
    return false;
  }

  if (drag.kind === 'item-group') {
    if (sections.length <= 1) {
      return false;
    }
    const fromIndex = sections.findIndex(
      section => isGroup(section) && section.id === drag.groupId,
    );
    if (fromIndex < 0) {
      return false;
    }
    if (isAdjacentNoOpSlot(slotIndex, fromIndex)) {
      return false;
    }
    return true;
  }

  if (drag.kind === 'item-row') {
    const rowIds = rowIdsInDragItem(drag);
    const indices = rowIds
      .map(rowId => {
        const located = locateRow(sections, rowId);
        if (!located || located.location.parent !== 'document') {
          return -1;
        }
        return located.location.sectionIndex;
      })
      .filter(index => index >= 0);
    if (indices.length === 0) {
      return false;
    }
    if (sections.length <= indices.length) {
      return false;
    }
    const minIndex = Math.min(...indices);
    const maxIndex = Math.max(...indices);
    if (slotIndex >= minIndex && slotIndex <= maxIndex + 1) {
      return false;
    }
    return true;
  }

  return true;
}

export function isGroupRowSlotEnabled(
  sections: DocumentSection[],
  slot: Extract<DropData, { kind: 'group-row-slot' }>,
  drag: DragData | undefined,
): boolean {
  if (!drag) {
    return false;
  }

  const group = sections.find(
    section => isGroup(section) && section.id === slot.groupId,
  );
  if (!group || !isGroup(group)) {
    return false;
  }

  if (isBlockDrag(drag)) {
    if (
      drag.kind === 'item-block' &&
      isBlockGroupRowSlotNoOp(sections, slot, drag)
    ) {
      return false;
    }
    return true;
  }

  if (drag.kind === 'palette-row') {
    return true;
  }

  if (drag.kind !== 'item-row') {
    return false;
  }

  const rowIds = rowIdsInDragItem(drag);
  if (rowIds.length > 1) {
    const indices = rowIds
      .map(rowId => {
        const located = locateRow(sections, rowId);
        if (
          !located ||
          located.location.parent !== 'group' ||
          located.location.groupId !== slot.groupId
        ) {
          return -1;
        }
        return located.location.rowIndex;
      })
      .filter(index => index >= 0);
    if (indices.length !== rowIds.length) {
      return false;
    }
    if (group.rows.length <= indices.length) {
      return false;
    }
    const minIndex = Math.min(...indices);
    const maxIndex = Math.max(...indices);
    if (slot.index >= minIndex && slot.index <= maxIndex + 1) {
      return false;
    }
    return true;
  }

  const located = locateRow(sections, drag.rowId);
  if (!located) {
    return false;
  }

  if (
    located.location.parent === 'group' &&
    located.location.groupId === slot.groupId
  ) {
    if (group.rows.length <= 1) {
      return false;
    }
    if (isAdjacentNoOpSlot(slot.index, located.location.rowIndex)) {
      return false;
    }
  }

  return true;
}

export function isBlockSlotEnabled(
  sections: DocumentSection[],
  slot: Extract<DropData, { kind: 'block-slot' }>,
  drag: DragData | undefined,
): boolean {
  if (!drag || !isBlockDrag(drag)) {
    return false;
  }

  if (slot.zone === 'fill') {
    const column = findColumnInSections(sections, slot.rowId, slot.columnId);
    return column ? column.blocks.length === 0 : false;
  }

  if (drag.kind === 'palette-block') {
    return true;
  }

  if (drag.kind !== 'item-block') {
    return false;
  }

  if (slot.rowId !== drag.rowId || slot.columnId !== drag.columnId) {
    return true;
  }

  const column = findColumnInSections(sections, slot.rowId, slot.columnId);
  const dragBlockIds = blockIdsInDragItem(drag);
  const indices = dragBlockIds
    .map(blockId => column?.blocks.findIndex(block => block.id === blockId) ?? -1)
    .filter(index => index >= 0);
  if (indices.length === 0) {
    return false;
  }

  const minIndex = Math.min(...indices);
  const maxIndex = Math.max(...indices);
  if (slot.index >= minIndex && slot.index <= maxIndex + 1) {
    return false;
  }

  return true;
}

export function isDropSlotEnabled(
  sections: DocumentSection[],
  slot: DropData,
  drag: DragData | undefined,
): boolean {
  if (slot.kind === 'section-slot') {
    return isSectionSlotEnabled(sections, slot.index, drag);
  }
  if (slot.kind === 'group-row-slot') {
    return isGroupRowSlotEnabled(sections, slot, drag);
  }
  return isBlockSlotEnabled(sections, slot, drag);
}

export function shouldHideFillDropZoneDuringDrag(
  sections: DocumentSection[],
  slot: DropData,
  drag: DragData | undefined,
): boolean {
  if (!drag) {
    return false;
  }

  if (slot.kind === 'block-slot' && slot.zone === 'fill') {
    if (drag.kind === 'item-row') {
      return slot.rowId === drag.rowId;
    }

    if (drag.kind === 'item-group') {
      const located = locateRow(sections, slot.rowId);
      return (
        located?.location.parent === 'group' &&
        located.location.groupId === drag.groupId
      );
    }
  }

  if (slot.kind === 'group-row-slot' && drag.kind === 'item-group') {
    return slot.groupId === drag.groupId;
  }

  return false;
}
